import * as Diff from 'diff'
import _ from 'lodash'

export type DiffFieldType = 'text' | 'markdown' | 'number' | 'boolean'

export type ObjectDiffArrayChange = {
  added?: boolean
  removed?: boolean
  value: string
}

export type ObjectDiffChange = {
  key: string
  type: 'added' | 'removed' | 'modified' | 'array'
  oldValue?: string | number | boolean
  newValue?: string | number | boolean
  changes?: ObjectDiffArrayChange[]
}

export type ObjectDiffAuditItem = {
  originalData?: Record<string, unknown> | null
  newData?: Record<string, unknown> | null
}

const isNullLike = (val: unknown) =>
  val === null ||
  val === undefined ||
  (typeof val === 'string' && val.trim().toLowerCase() === 'null')

function serializeDiffValue(value: unknown): string | number | boolean {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  return JSON.stringify(value)
}

export function diffObjects(
  originalData: Record<string, unknown> = {},
  newData: Record<string, unknown> = {},
  options?: { skipKeys?: string[] },
): ObjectDiffChange[] {
  const changes: ObjectDiffChange[] = []
  const skipKeys = new Set(options?.skipKeys ?? ['updated_at'])
  const allKeys = new Set([...Object.keys(originalData), ...Object.keys(newData)])

  for (const key of allKeys) {
    if (skipKeys.has(key)) continue

    const oldValue = originalData[key]
    const newValue = newData[key]

    if (_.isEqual(oldValue, newValue)) continue

    if (Array.isArray(oldValue) || Array.isArray(newValue)) {
      const arrayChanges: ObjectDiffArrayChange[] = []
      const oldArray = Array.isArray(oldValue) ? oldValue : []
      const newArray = Array.isArray(newValue) ? newValue : []

      newArray.forEach((arrayItem: unknown) => {
        if (isNullLike(arrayItem)) return
        if (!oldArray.some((oldItem: unknown) => _.isEqual(oldItem, arrayItem))) {
          arrayChanges.push({ added: true, value: JSON.stringify(arrayItem) })
        }
      })

      oldArray.forEach((arrayItem: unknown) => {
        if (!newArray.some((newItem: unknown) => _.isEqual(newItem, arrayItem))) {
          arrayChanges.push({ removed: true, value: JSON.stringify(arrayItem) })
        }
      })

      if (arrayChanges.length > 0) {
        changes.push({ key, type: 'array', changes: arrayChanges })
      }
      continue
    }

    if (oldValue === undefined) {
      if (!isNullLike(newValue)) {
        changes.push({
          key,
          type: 'added',
          newValue: serializeDiffValue(newValue),
        })
      }
    } else if (newValue === undefined) {
      changes.push({
        key,
        type: 'removed',
        oldValue: serializeDiffValue(oldValue),
      })
    } else {
      changes.push({
        key,
        type: 'modified',
        oldValue: serializeDiffValue(oldValue),
        newValue: serializeDiffValue(newValue),
      })
    }
  }

  return changes
}

export function renderAuditLogDiff(item: ObjectDiffAuditItem): ObjectDiffChange[] {
  return diffObjects(item.originalData ?? {}, item.newData ?? {})
}

export type FieldRegistryEntry<T extends string = string> = {
  key: T
  type: DiffFieldType
}

export function normalizeDiffValue(value: unknown, type: DiffFieldType): string {
  if (value === null || value === undefined) return ''
  if (type === 'number') {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? String(numeric) : ''
  }
  if (type === 'boolean') {
    return value === true || value === 'true' ? 'true' : 'false'
  }
  return String(value).trim()
}

export function valuesDiffer(
  baseline: unknown,
  current: unknown,
  type: DiffFieldType,
): boolean {
  return normalizeDiffValue(baseline, type) !== normalizeDiffValue(current, type)
}

export function diffFields<T extends Record<string, unknown>>(
  baseline: T | null | undefined,
  current: T | null | undefined,
  registry: FieldRegistryEntry<keyof T & string>[],
): Partial<Record<keyof T & string, { type: DiffFieldType; baseline: unknown; current: unknown }>> {
  const result: Partial<
    Record<keyof T & string, { type: DiffFieldType; baseline: unknown; current: unknown }>
  > = {}

  for (const field of registry) {
    const baselineValue = baseline?.[field.key]
    const currentValue = current?.[field.key]
    if (!valuesDiffer(baselineValue, currentValue, field.type)) continue
    result[field.key] = {
      type: field.type,
      baseline: baselineValue,
      current: currentValue,
    }
  }

  return result
}

export function diffTextInline(baseline: string, current: string): string {
  const parts = Diff.diffWords(baseline, current)
  return parts
    .map((part) => {
      if (part.added) {
        return `<span class="diff-inline-added">${_.escape(part.value)}</span>`
      }
      if (part.removed) {
        return `<span class="diff-inline-removed">${_.escape(part.value)}</span>`
      }
      return _.escape(part.value)
    })
    .join('')
}
